import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { User, Lock, ArrowLeft, Eye, EyeOff } from "lucide-react";

// Spardom Splash Logo Component - Large dominant hero element
const SpardomSplashLogo = () => (
  <div className="flex flex-col items-center">
    <img 
      src="/spardom-logo.png" 
      alt="Spardom" 
      className="h-72 sm:h-80 md:h-96 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
    />
  </div>
);

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Use timeout and explicit headers for better PWA compatibility
      const response = await axios.post(
        `${API}/auth/login`, 
        { email, password },
        {
          timeout: 15000, // 15 second timeout for PWA/slow networks
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );
      
      if (response.data?.user && response.data?.token) {
        login(response.data.user, response.data.token);
        toast.success("Welcome back!");
        navigate("/browse");
      } else {
        // Unexpected response format
        console.error("[Login] Unexpected response:", response.data);
        toast.error("Login failed - unexpected response");
      }
    } catch (err) {
      console.error("[Login] Error:", err.message, err.code, err.response?.status);
      
      // Provide more specific error messages
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Connection timed out. Please check your internet and try again.");
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        toast.error("Network error. Please check your connection and try again.");
      } else if (err.response?.status === 401) {
        toast.error("Invalid email or password");
      } else if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || "Account disabled");
      } else if (err.response?.status === 429) {
        toast.error("Too many attempts. Please wait a moment and try again.");
      } else {
        toast.error(err.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/browse';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Neutral dark arena background with subtle ember glow */}
      <div className="absolute inset-0 bg-[#0B0F14]">
        {/* Subtle ember accent glow at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[40%] bg-gradient-to-b from-[#FF7A18]/10 via-[#FF7A18]/5 to-transparent blur-3xl" />
        {/* Subtle bottom glow */}
        <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-t from-[#182331]/50 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col px-6 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),20px)]">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#B7C3D0] hover:text-white group transition-colors text-sm w-fit font-medium"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </Link>

        {/* Logo - Large dominant visual anchor */}
        <div className="flex justify-center mt-2 mb-8">
          <SpardomSplashLogo />
        </div>

        {/* Sign In Section */}
        <div className="w-full max-w-sm mx-auto flex-1 flex flex-col">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 drop-shadow-lg">Sign In</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username/Email Field */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A96A6]">
                <User className="w-5 h-5" />
              </div>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 h-14 bg-[#121A22] border-[#263445] text-white placeholder:text-[#8A96A6] focus:border-[#FF7A18] focus:ring-1 focus:ring-[#FF7A18]/30 rounded-full text-base font-medium shadow-lg"
                required
              />
            </div>

            {/* Password Field */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A96A6]">
                <Lock className="w-5 h-5" />
              </div>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                data-testid="login-password"
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-12 pr-12 h-14 bg-[#121A22] border-[#263445] text-white placeholder:text-[#8A96A6] focus:border-[#FF7A18] focus:ring-1 focus:ring-[#FF7A18]/30 rounded-full text-base font-medium shadow-lg"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A96A6] hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <button
                type="button"
                onClick={() => toast.info("Please contact an admin to reset your password.", { duration: 5000 })}
                className="text-sm text-[#8A96A6] hover:text-[#FF9A4A] transition-colors font-medium"
              >
                Forgot password?
              </button>
            </div>

            {/* Sign In Button - Ember accent */}
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-[#FF7A18] hover:bg-[#FF9A4A] text-white h-14 font-bold uppercase tracking-widest rounded-full transition-all shadow-xl shadow-[#FF7A18]/20 text-base"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          {/* Social Login Section */}
          <div className="mt-8">
            <p className="text-center text-[#B7C3D0] mb-4 text-sm font-medium">Sign In With Social Media</p>
            
            <div className="flex justify-center">
              {/* Google Button */}
              <button
                type="button"
                data-testid="google-login"
                onClick={handleGoogleLogin}
                className="flex items-center gap-3 text-white hover:text-white/90 transition-colors bg-[#182331] hover:bg-[#263445] border border-[#263445] px-8 py-3 rounded-full"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <span className="text-sm uppercase tracking-wide font-semibold">Google</span>
              </button>
            </div>
          </div>

          {/* Sign Up Link */}
          <p className="text-center text-[#B7C3D0] mt-auto pt-6 text-sm font-medium">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-[#FF7A18] hover:text-[#FF9A4A] hover:underline font-bold transition-colors">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
